// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Meta, National, Postcode } from './types';

export const STATE_COLOURS: Record<string, string> = {
  NSW: '#2563eb',
  VIC: '#7c3aed',
  QLD: '#ea580c',
  SA: '#dc2626',
  WA: '#0d9488',
  TAS: '#65a30d',
  NT: '#d97706',
  ACT: '#db2777',
};
export const STATE_ORDER = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
export function stateColour(st: string): string {
  return STATE_COLOURS[st] ?? '#94a3b8';
}

export interface Dataset {
  postcodes: Postcode[];
  meta: Meta;
  national: National;
  byPostcode: Map<string, Postcode>;
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Could not load ${url} (HTTP ${res.status})`);
  return (await res.json()) as T;
}

export async function loadDataset(signal?: AbortSignal): Promise<Dataset> {
  const [postcodes, meta, national] = await Promise.all([
    fetchJson<Postcode[]>('data/postcodes.json', signal),
    fetchJson<Meta>('data/meta.json', signal),
    fetchJson<National>('data/national.json', signal),
  ]);
  const byPostcode = new Map(postcodes.map((p) => [p.pc, p]));
  return { postcodes, meta, national, byPostcode };
}

let geoPromise: Promise<GeoJSON.FeatureCollection> | null = null;
export function loadGeo(signal?: AbortSignal): Promise<GeoJSON.FeatureCollection> {
  if (!geoPromise) {
    geoPromise = fetchJson<GeoJSON.FeatureCollection>('data/poa.geojson', signal).catch((err) => {
      geoPromise = null;
      throw err;
    });
  }
  return geoPromise;
}

/** Search postcodes by code or locality name. */
export function searchPostcodes(all: Postcode[], query: string, limit = 8): Postcode[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const scored: Array<{ p: Postcode; score: number }> = [];
  for (const p of all) {
    let score = -1;
    if (p.pc === q) score = 100;
    else if (p.pc.startsWith(q)) score = 80;
    else {
      const hit = p.locs.find((l) => l.toLowerCase().startsWith(q));
      if (hit) score = 60 - hit.length * 0.01;
      else if (p.locs.some((l) => l.toLowerCase().includes(q))) score = 30;
    }
    if (score > 0) scored.push({ p, score: score + Math.min(p.total, 50000) / 1e6 });
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.p);
}
